/* ═══════════════════════════════════════════════════════════════
   Admin Center — Roles, Bill Rates, Lookup Tables, CSV Import
   ═══════════════════════════════════════════════════════════════ */

import React, { useState, useEffect } from 'react';
import {
  Trash2,
  ChevronRight,
  ArrowLeft,
  Search,
  X,
  Upload,
  Check,
  Minus,
  GripVertical,
} from 'lucide-react';
import { SearchableSelect } from '../components/SearchableSelect';
import { Switch } from '../components/ui/switch';
import EmptyState from '../components/EmptyState';
import { TITLE_COLORS, DEPARTMENT_COLORS, DEFAULT_SCOPE_COLORS } from '../lib/palette';
import { AdminService } from '../services/adminService';
import { TeamService } from '../services/teamService';
import { OrgService } from '../services/orgService';
import { ProjectService } from '../services/projectService';
// All data goes through cloud API via services
import type {
  Role,
  ProjectType,
  BillingType,
  ActivityCode,
  ProjectScope,
  BillingRole,
  FirmHoliday,
} from '../../shared/types';
import { MODULE_DEFS, type AppModule } from '../../shared/modules';
import {
  PERMISSION_CATEGORIES,
  ROLE_PRESETS,
  BASE_PERMISSIONS,
  permissionsToLegacy,
  ALL_PERMISSIONS,
  hasPermission,
} from '../../shared/permissions';
import { VacationService } from '../services/vacationService';
import { DepartmentService } from '../services/departmentService';
import * as cloudCache from '../services/cloudCache';
import { useTableSync } from '../hooks/useTableSync';
import type { Department } from '../../shared/entities';
import { FirmActivityPanel } from '../components/FirmActivityPanel';
import { HarSyncTab } from './HarSyncTab';
import { canHarSync } from '../services/harSyncService';

interface AdminPageProps {
  toast: (msg: string, isError?: boolean) => void;
  userPermissions?: string[];
  userEmail?: string;
}

type Tab =
  | 'roles'
  | 'billingRoles'
  | 'departments'
  | 'projectTypes'
  | 'billingTypes'
  | 'activityCodes'
  | 'phases'
  | 'overheadCategories'
  | 'expenseTypes'
  | 'scopes'
  | 'vacationCategories'
  | 'firmHolidays'
  | 'import'
  | 'harSync'
  | 'subscription'
  | 'activity';
// 'companyRoles' removed in v2.9.0 — merged into Titles (the renamed
// Billing Roles tab). The dead CompanyRolesTab component was deleted
// from this file during the spec-masters-repair pass.

// ─── Tab groups: each group has a permission gate and a set of tabs ───
interface TabGroup {
  label: string;
  /** Permission key required to see this group. null = always visible if user can access admin center. */
  permission: string | null;
  tabs: { id: Tab; label: string }[];
}

const TAB_GROUPS: TabGroup[] = [
  {
    label: 'Roles & Access',
    permission: 'admin_center.manage_roles',
    tabs: [
      { id: 'roles', label: 'Roles & Privileges' },
      { id: 'departments', label: 'Departments' },
    ],
  },
  {
    label: 'Billing & Project Setup',
    permission: 'admin_center.manage_billing',
    tabs: [
      { id: 'billingRoles', label: 'Titles' },
      { id: 'projectTypes', label: 'Project Types' },
      { id: 'billingTypes', label: 'Fee Structures' },
      { id: 'activityCodes', label: 'Billing Codes' },
      { id: 'phases', label: 'Phases' },
      { id: 'overheadCategories', label: 'Overhead Categories' },
      { id: 'expenseTypes', label: 'Expense Types' },
      { id: 'scopes', label: 'Project Scopes' },
    ],
  },
  {
    label: 'Time Off',
    permission: 'admin_center.manage_billing',
    tabs: [
      { id: 'vacationCategories', label: 'Vacation Categories' },
      { id: 'firmHolidays', label: 'Firm Holidays' },
    ],
  },
  {
    label: 'Data',
    permission: 'admin_center.manage_billing',
    tabs: [{ id: 'import', label: 'CSV Import' }],
  },
  {
    label: 'Subscription',
    permission: 'admin_center.manage_subscription',
    tabs: [{ id: 'subscription', label: 'Subscription' }],
  },
  {
    label: 'Audit',
    permission: 'audit_log.view_all',
    tabs: [{ id: 'activity', label: 'Activity Log' }],
  },
];

const inputStyle: React.CSSProperties = {
  padding: '7px 10px',
  borderRadius: 5,
  border: '1px solid var(--bd)',
  background: 'var(--ib)',
  color: 'var(--tx)',
  fontSize: 13,
  width: '100%',
};
const btnPrimary: React.CSSProperties = {
  padding: '7px 16px',
  borderRadius: 6,
  border: 'none',
  background: 'var(--ac)',
  color: 'var(--color-text-inverse)',
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer',
};
const btnDanger: React.CSSProperties = {
  padding: '5px 10px',
  borderRadius: 4,
  border: 'none',
  background: 'transparent',
  color: 'var(--dg)',
  fontSize: 12,
  cursor: 'pointer',
};
const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '8px 12px',
  fontSize: 11,
  fontWeight: 700,
  color: 'var(--tm)',
  textTransform: 'uppercase',
  letterSpacing: '.04em',
  borderBottom: '1px solid var(--bd)',
  background: 'var(--sa)',
};
const tdStyle: React.CSSProperties = {
  padding: '8px 12px',
  borderBottom: '1px solid var(--bd)',
  fontSize: 13,
};

/* ─── Drag-to-reorder helpers ───
 * Every admin lookup list uses the same pattern:
 *   1. User drags a row by its handle.
 *   2. On drop, compute the new order, write `sortOrder = index` to every row
 *      that actually moved, persist via the table's service.save.
 * The hook keeps the drag state local to each table instance so two tables on
 * screen can't fight over it. The drag handle cell is a small vertical-dots
 * glyph the user can grab; the row is the drop target. */

interface DragHandlers<T extends { id: string }> {
  dragIdx: number | null;
  overIdx: number | null;
  rowProps: (i: number) => React.HTMLAttributes<HTMLElement>;
  handleProps: () => React.HTMLAttributes<HTMLElement>;
}

function useDragReorder<T extends { id: string; sortOrder?: number }>(
  items: T[],
  onReorder: (reordered: T[]) => Promise<void> | void
): DragHandlers<T> {
  const [dragIdx, setDragIdx] = React.useState<number | null>(null);
  const [overIdx, setOverIdx] = React.useState<number | null>(null);

  async function commit(from: number, to: number) {
    setDragIdx(null);
    setOverIdx(null);
    if (from === to || from < 0 || to < 0 || from >= items.length || to >= items.length) return;
    const next = items.slice();
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    const withOrder = next.map((it, i) => ({ ...it, sortOrder: i }));
    await onReorder(withOrder);
  }

  return {
    dragIdx,
    overIdx,
    rowProps: (i: number) => ({
      // The handle sets draggable=true so clicks elsewhere on the row don't start a drag.
      onDragOver: (e: React.DragEvent) => {
        if (dragIdx === null) return;
        e.preventDefault();
        if (overIdx !== i) setOverIdx(i);
      },
      onDrop: (e: React.DragEvent) => {
        e.preventDefault();
        if (dragIdx !== null) commit(dragIdx, i);
      },
      'data-drag-idx': i as any,
    }),
    handleProps: () => ({
      draggable: true,
      onDragStart: (e: React.DragEvent) => {
        // `currentTarget` is the handle itself; walk up to the row to read its index.
        const row = (e.currentTarget as HTMLElement).closest('[data-drag-idx]');
        const idx = row ? parseInt(row.getAttribute('data-drag-idx') || '-1', 10) : -1;
        if (idx >= 0) setDragIdx(idx);
        e.dataTransfer.effectAllowed = 'move';
      },
      onDragEnd: () => {
        setDragIdx(null);
        setOverIdx(null);
      },
      style: { cursor: 'grab' } as React.CSSProperties,
    }),
  };
}

// The grip glyph used consistently across every drag handle.
const DRAG_HANDLE_STYLE: React.CSSProperties = {
  width: 18,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'var(--tm)',
  fontSize: 16,
  lineHeight: 1,
  userSelect: 'none',
  flexShrink: 0,
};

/** Shared row-skeleton shown during a tab's first data load (skeletons over
 *  blocking spinners — DESIGN.md §4). Mirrors the row rhythm of the simple
 *  list tables this file renders. */
function RowSkeleton({ rows = 4, label }: { rows?: number; label: string }) {
  return (
    <div
      role="status"
      aria-label={label}
      style={{
        background: 'var(--sf)',
        borderRadius: 8,
        border: '1px solid var(--bd)',
        overflow: 'hidden',
        padding: 12,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
      }}
    >
      {Array.from({ length: rows }).map((_, i) => (
        <div
          key={i}
          style={{
            height: 32,
            background: 'var(--sa)',
            borderRadius: 6,
            opacity: Math.max(0.3, 0.95 - i * 0.15),
            animation: 'pulse 1.4s ease-in-out infinite',
          }}
        />
      ))}
    </div>
  );
}

/* ─── Roles Tab ─── */
function RolesTab({ toast }: { toast: AdminPageProps['toast'] }) {
  const [roles, setRoles] = useState<Role[]>([]);
  const [editing, setEditing] = useState<Role | null>(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  // Editor UI state — kept here so search query and expand state survive
  // checkbox toggles without re-rendering the whole permission catalog.
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCats, setExpandedCats] = useState<Set<string>>(new Set());
  const [pendingPreset, setPendingPreset] = useState<string | null>(null);

  // Reset transient editor state whenever we open/close a different role.
  useEffect(() => {
    setSearchQuery('');
    setExpandedCats(new Set());
    setPendingPreset(null);
  }, [editing?.id]);

  useEffect(() => {
    load();
  }, []);
  useTableSync(['roles'], () => load());
  async function load() {
    try {
      const all = await AdminService.listRoles();
      all.sort((a, b) => (a.sortOrder ?? 99) - (b.sortOrder ?? 99));
      setRoles(all);
    } finally {
      setLoading(false);
    }
  }

  function empty(): Role {
    const perms = [
      ...BASE_PERMISSIONS,
      'specs.view',
      'specs.create',
      'specs.edit_own',
      'team_members.view',
      'tasks.edit_own',
      'files.upload',
    ];
    return {
      id: '',
      name: '',
      accessLevel: 'member',
      permissions: perms,
      ...permissionsToLegacy(perms),
      sortOrder: roles.length,
      createdAt: '',
      updatedAt: '',
    };
  }

  async function save() {
    if (!editing || saving) return;
    if (!editing.name.trim()) {
      toast('Enter a role name', true);
      return;
    }
    setSaving(true);
    try {
      const legacy = permissionsToLegacy(editing.permissions || []);
      const toSave = { ...editing, ...legacy };
      await AdminService.saveRole(toSave);
      await load();
      setEditing(null);
      toast('Role saved');
    } catch (err: any) {
      console.error('[RolesTab] Save failed:', err);
      toast('Save failed: ' + (err?.message || String(err)), true);
    } finally {
      setSaving(false);
    }
  }

  function isRequired(_name: string) {
    return false;
  }

  async function remove(id: string) {
    const role = roles.find((r) => r.id === id);
    if (role && isRequired(role.name)) {
      toast('This is a required role and cannot be deleted.', true);
      return;
    }
    if (!confirm('Delete this role? Team members assigned to it will have their role unset.'))
      return;
    try {
      await AdminService.deleteRole(id);
      if (editing?.id === id) setEditing(null);
      await load();
      toast('Role deleted');
    } catch (err: any) {
      toast('Delete failed: ' + err.message, true);
    }
  }

  function togglePerm(key: string) {
    setEditing((prev) => {
      if (!prev) return prev;
      const perms = prev.permissions || [];
      const next = perms.includes(key) ? perms.filter((p) => p !== key) : [...perms, key];
      return { ...prev, permissions: next };
    });
  }

  /** Click handler for preset chips. For brand-new roles (no persisted id)
   *  we apply silently because there's nothing to clobber yet. For existing
   *  roles, surface a diff-aware confirm bar so a stray click can't wipe
   *  out hand-tuned permissions. */
  function requestPreset(presetId: string) {
    const preset = ROLE_PRESETS[presetId];
    if (!preset || !editing) return;
    const current = new Set(editing.permissions || []);
    const target = new Set(preset.permissions);
    const sameSize = current.size === target.size;
    const sameContents = sameSize && [...current].every((k) => target.has(k));
    if (sameContents) return; // already on this preset
    if (!editing.id) {
      // Fresh role — no risk of clobbering meaningful work.
      setEditing({ ...editing, permissions: [...preset.permissions] });
      return;
    }
    setPendingPreset(presetId);
  }

  function confirmPendingPreset() {
    if (!pendingPreset || !editing) return;
    const preset = ROLE_PRESETS[pendingPreset];
    if (!preset) return;
    setEditing({ ...editing, permissions: [...preset.permissions] });
    setPendingPreset(null);
  }

  function expandAllCats() {
    setExpandedCats(new Set(PERMISSION_CATEGORIES.map((c) => c.id)));
  }
  function collapseAllCats() {
    setExpandedCats(new Set());
  }
  function toggleCatExpansion(catId: string) {
    setExpandedCats((prev) => {
      const next = new Set(prev);
      if (next.has(catId)) next.delete(catId);
      else next.add(catId);
      return next;
    });
  }

  function toggleCategory(catId: string) {
    if (!editing) return;
    const cat = PERMISSION_CATEGORIES.find((c) => c.id === catId);
    if (!cat) return;
    const perms = editing.permissions || [];
    const allEnabled = cat.permissions.every((p) => perms.includes(p.key));
    if (allEnabled) {
      // Remove all in category (except base permissions)
      const catKeys = new Set(cat.permissions.map((p) => p.key));
      const baseSet = new Set(BASE_PERMISSIONS);
      setEditing({
        ...editing,
        permissions: perms.filter((p) => !catKeys.has(p) || baseSet.has(p)),
      });
    } else {
      // Add all in category
      const next = new Set(perms);
      cat.permissions.forEach((p) => next.add(p.key));
      setEditing({ ...editing, permissions: [...next] });
    }
  }

  const checkboxStyle = (checked: boolean): React.CSSProperties => ({
    width: 16,
    height: 16,
    borderRadius: 3,
    flexShrink: 0,
    border: `2px solid ${checked ? 'var(--ac)' : 'var(--bd)'}`,
    background: checked ? 'var(--ac)' : 'transparent',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'var(--color-text-inverse)',
    fontSize: 10,
    cursor: 'pointer',
  });

  if (editing) {
    const perms = editing.permissions || [];
    return (
      <div style={{ maxWidth: 700 }}>
        <button
          onClick={() => setEditing(null)}
          style={{
            ...btnDanger,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            marginBottom: 12,
            color: 'var(--tm)',
            border: '1px solid var(--bd)',
          }}
        >
          <ArrowLeft size={13} /> Back to Roles
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, margin: 0, flex: 1 }}>
            {editing.id ? 'Edit Role' : 'New Role'}
          </h3>
          <button
            onClick={save}
            disabled={saving}
            style={{ ...btnPrimary, opacity: saving ? 0.6 : 1 }}
          >
            {saving ? 'Saving...' : 'Save Role'}
          </button>
          {editing.id && (
            <button onClick={() => remove(editing.id)} style={btnDanger}>
              Delete
            </button>
          )}
        </div>

        <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
          <label style={{ flex: 1 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--tm)', marginBottom: 4 }}>
              Role Name
            </div>
            <input
              value={editing.name}
              onChange={(e) =>
                setEditing((prev) => (prev ? { ...prev, name: e.target.value } : prev))
              }
              style={inputStyle}
            />
          </label>
          <label style={{ width: 100 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--tm)', marginBottom: 4 }}>
              Sort Order
            </div>
            <input
              type="number"
              min="0"
              value={editing.sortOrder}
              onChange={(e) =>
                setEditing((prev) =>
                  prev ? { ...prev, sortOrder: parseInt(e.target.value) || 0 } : prev
                )
              }
              style={inputStyle}
            />
          </label>
        </div>

        {/* \u2500\u2500\u2500 Quick Presets \u2500\u2500\u2500 */}
        {(() => {
          const currentSet = new Set(perms);
          const matchingPresetId = Object.entries(ROLE_PRESETS).find(([, preset]) => {
            const t = new Set(preset.permissions);
            return t.size === currentSet.size && [...currentSet].every((k) => t.has(k));
          })?.[0];
          return (
            <div style={{ marginBottom: 16 }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'baseline',
                  justifyContent: 'space-between',
                  marginBottom: 6,
                }}
              >
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--tm)' }}>
                  {editing.id ? 'Apply a preset' : 'Start from a preset'}
                </div>
                {matchingPresetId && (
                  <div style={{ fontSize: 10, color: 'var(--tm)', fontStyle: 'italic' }}>
                    matches {ROLE_PRESETS[matchingPresetId].label}
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {Object.entries(ROLE_PRESETS).map(([id, preset]) => {
                  const isCurrent = matchingPresetId === id;
                  return (
                    <button
                      key={id}
                      onClick={() => requestPreset(id)}
                      disabled={isCurrent}
                      title={
                        isCurrent
                          ? 'Already applied'
                          : `Apply ${preset.permissions.length} permissions from ${preset.label}`
                      }
                      style={{
                        padding: '5px 12px',
                        borderRadius: 999,
                        border: `1px solid ${isCurrent ? 'var(--ac)' : 'var(--bd)'}`,
                        background: isCurrent
                          ? 'color-mix(in srgb, var(--ac) 12%, transparent)'
                          : 'var(--sa)',
                        color: isCurrent ? 'var(--ac)' : 'var(--tx)',
                        fontSize: 11,
                        fontWeight: isCurrent ? 700 : 500,
                        cursor: isCurrent ? 'default' : 'pointer',
                        transition: 'all .12s',
                      }}
                    >
                      {preset.label}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })()}

        {/* \u2500\u2500\u2500 Pending preset confirm bar \u2500\u2500\u2500 */}
        {pendingPreset &&
          (() => {
            const preset = ROLE_PRESETS[pendingPreset];
            if (!preset) return null;
            const current = new Set(perms);
            const target = new Set(preset.permissions);
            const adding = preset.permissions.filter((k) => !current.has(k)).length;
            const removing = perms.filter((k) => !target.has(k)).length;
            return (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '12px 14px',
                  marginBottom: 16,
                  background: 'color-mix(in srgb, var(--ac) 8%, transparent)',
                  border: '1px solid var(--ac)',
                  borderRadius: 8,
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{ fontSize: 13, fontWeight: 600, color: 'var(--tx)', marginBottom: 2 }}
                  >
                    Replace current permissions with {preset.label}?
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--tm)' }}>
                    +{adding} added, -{removing} removed.{' '}
                    {removing > 0 ? 'This cannot be undone after saving.' : ''}
                  </div>
                </div>
                <button
                  onClick={() => setPendingPreset(null)}
                  style={{
                    padding: '6px 14px',
                    borderRadius: 6,
                    border: '1px solid var(--bd)',
                    background: 'var(--sf)',
                    color: 'var(--tx)',
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={confirmPendingPreset}
                  style={{
                    padding: '6px 14px',
                    borderRadius: 6,
                    border: 'none',
                    background: 'var(--ac)',
                    color: 'var(--color-text-inverse)',
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  Replace
                </button>
              </div>
            );
          })()}

        {/* \u2500\u2500\u2500 Permissions header \u2500\u2500\u2500 */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            marginBottom: 8,
          }}
        >
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--tx)' }}>
            Permissions{' '}
            <span style={{ fontWeight: 400, color: 'var(--tm)' }}>
              ({perms.length} of {ALL_PERMISSIONS.length})
            </span>
          </div>
          <div style={{ flex: 1 }} />
          <button
            onClick={expandAllCats}
            style={{
              padding: '4px 10px',
              borderRadius: 4,
              border: '1px solid var(--bd)',
              background: 'var(--sf)',
              color: 'var(--tm)',
              fontSize: 11,
              cursor: 'pointer',
            }}
          >
            Expand all
          </button>
          <button
            onClick={collapseAllCats}
            style={{
              padding: '4px 10px',
              borderRadius: 4,
              border: '1px solid var(--bd)',
              background: 'var(--sf)',
              color: 'var(--tm)',
              fontSize: 11,
              cursor: 'pointer',
            }}
          >
            Collapse all
          </button>
        </div>

        {/* \u2500\u2500\u2500 Search \u2500\u2500\u2500 */}
        <div style={{ position: 'relative', marginBottom: 12 }}>
          <input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search permissions by name, description, or key..."
            style={{
              ...inputStyle,
              paddingLeft: 32,
              fontSize: 12,
            }}
          />
          <span
            style={{
              position: 'absolute',
              left: 10,
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'var(--tm)',
              display: 'inline-flex',
              pointerEvents: 'none',
            }}
            aria-hidden
          >
            <Search size={13} />
          </span>
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              title="Clear search"
              aria-label="Clear search"
              style={{
                position: 'absolute',
                right: 4,
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'transparent',
                border: 'none',
                color: 'var(--tm)',
                cursor: 'pointer',
                padding: 9,
                lineHeight: 1,
                display: 'inline-flex',
              }}
            >
              <X size={14} />
            </button>
          )}
        </div>

        {/* \u2500\u2500\u2500 Categories \u2500\u2500\u2500 */}
        {(() => {
          const q = searchQuery.trim().toLowerCase();
          const matchedCats = PERMISSION_CATEGORIES.map((cat) => {
            const matchedPerms = q
              ? cat.permissions.filter(
                  (p) =>
                    p.label.toLowerCase().includes(q) ||
                    p.key.toLowerCase().includes(q) ||
                    (p.description || '').toLowerCase().includes(q) ||
                    cat.label.toLowerCase().includes(q)
                )
              : cat.permissions;
            return { cat, matchedPerms };
          }).filter(({ matchedPerms }) => matchedPerms.length > 0);

          if (matchedCats.length === 0) {
            return (
              <div
                style={{
                  padding: 32,
                  textAlign: 'center',
                  color: 'var(--tm)',
                  fontSize: 13,
                  background: 'var(--sa)',
                  borderRadius: 8,
                  border: '1px dashed var(--bd)',
                  marginBottom: 20,
                }}
              >
                No permissions match &ldquo;{searchQuery}&rdquo;
              </div>
            );
          }

          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20 }}>
              {matchedCats.map(({ cat, matchedPerms }) => {
                const catKeys = matchedPerms.map((p) => p.key);
                const enabledCount = catKeys.filter((k) => perms.includes(k)).length;
                const allEnabled = enabledCount === matchedPerms.length;
                const someEnabled = enabledCount > 0 && !allEnabled;
                // Search auto-expands matching categories so the user can see why they matched.
                const isExpanded = q.length > 0 || expandedCats.has(cat.id);

                return (
                  <div
                    key={cat.id}
                    style={{
                      background: 'var(--sa)',
                      borderRadius: 8,
                      border: '1px solid var(--bd)',
                      overflow: 'hidden',
                    }}
                  >
                    {/* Header */}
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        padding: '10px 12px',
                        borderBottom: isExpanded ? '1px solid var(--bd)' : 'none',
                      }}
                    >
                      {/* Master checkbox toggles enable for the WHOLE category. We
                           stop propagation so the click doesn't also toggle
                           expansion \u2014 separation of intent prevents double-action
                           confusion. */}
                      <div
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleCategory(cat.id);
                        }}
                        role="checkbox"
                        aria-checked={someEnabled && !allEnabled ? 'mixed' : allEnabled}
                        aria-label={
                          allEnabled ? 'Disable all in category' : 'Enable all in category'
                        }
                        style={{
                          ...checkboxStyle(allEnabled),
                          background: allEnabled
                            ? 'var(--ac)'
                            : someEnabled
                              ? 'var(--ac)'
                              : 'transparent',
                          opacity: someEnabled && !allEnabled ? 0.5 : 1,
                        }}
                        title={allEnabled ? 'Disable all in category' : 'Enable all in category'}
                      >
                        {allEnabled ? (
                          <Check size={11} strokeWidth={3} />
                        ) : someEnabled ? (
                          <Minus size={11} strokeWidth={3} />
                        ) : (
                          ''
                        )}
                      </div>

                      {/* Name + chevron \u2014 clicking this region expands/collapses */}
                      <div
                        onClick={() => toggleCatExpansion(cat.id)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          flex: 1,
                          cursor: 'pointer',
                          minWidth: 0,
                        }}
                      >
                        <span
                          style={{
                            display: 'inline-flex',
                            width: 12,
                            color: 'var(--tm)',
                            transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                            transition: 'transform .15s ease',
                          }}
                          aria-hidden
                        >
                          <ChevronRight size={12} />
                        </span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--tx)' }}>
                            {cat.label}
                          </div>
                          <div
                            style={{
                              fontSize: 10,
                              color: 'var(--tm)',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {cat.description}
                          </div>
                        </div>
                      </div>

                      {/* Count badge */}
                      <span
                        style={{
                          fontSize: 10,
                          fontWeight: 700,
                          color: enabledCount === 0 ? 'var(--tm)' : 'var(--ac)',
                          background:
                            enabledCount === 0
                              ? 'transparent'
                              : 'color-mix(in srgb, var(--ac) 12%, transparent)',
                          padding: '3px 8px',
                          borderRadius: 999,
                          border:
                            enabledCount === 0
                              ? '1px solid var(--bd)'
                              : '1px solid color-mix(in srgb, var(--ac) 40%, transparent)',
                          fontVariantNumeric: 'tabular-nums',
                        }}
                      >
                        {enabledCount}/{matchedPerms.length}
                      </span>
                    </div>

                    {/* Permissions grid */}
                    {isExpanded && (
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0 }}>
                        {matchedPerms.map((p) => {
                          const checked = perms.includes(p.key);
                          return (
                            <div
                              key={p.key}
                              onClick={() => togglePerm(p.key)}
                              style={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: 8,
                                padding: '8px 12px',
                                cursor: 'pointer',
                                borderTop: '1px solid var(--bd)',
                                transition: 'background .1s',
                              }}
                              onMouseEnter={(e) =>
                                (e.currentTarget.style.background =
                                  'color-mix(in srgb, var(--ac) 4%, transparent)')
                              }
                              onMouseLeave={(e) => (e.currentTarget.style.background = '')}
                            >
                              <div
                                role="checkbox"
                                aria-checked={checked}
                                aria-label={p.label}
                                style={{ ...checkboxStyle(checked), marginTop: 2 }}
                              >
                                {checked ? <Check size={11} strokeWidth={3} /> : ''}
                              </div>
                              <div style={{ minWidth: 0, flex: 1 }}>
                                <div style={{ fontSize: 12, color: 'var(--tx)', fontWeight: 500 }}>
                                  {p.label}
                                </div>
                                {p.description && (
                                  <div
                                    style={{
                                      fontSize: 11,
                                      color: 'var(--tm)',
                                      marginTop: 2,
                                      lineHeight: 1.4,
                                    }}
                                  >
                                    {p.description}
                                  </div>
                                )}
                                <div
                                  style={{
                                    fontSize: 10,
                                    color: 'var(--tm)',
                                    fontFamily: "'JetBrains Mono', monospace",
                                    marginTop: 2,
                                    opacity: 0.6,
                                  }}
                                >
                                  {p.key}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })()}

        <div style={{ display: 'flex', gap: 8, padding: '12px 0' }}>
          <button
            onClick={save}
            disabled={saving}
            style={{ ...btnPrimary, opacity: saving ? 0.6 : 1 }}
          >
            {saving ? 'Saving...' : 'Save Role'}
          </button>
          {editing.id && (
            <button onClick={() => remove(editing.id)} style={btnDanger}>
              Delete
            </button>
          )}
        </div>
      </div>
    );
  }

  // ─── Role list view ───
  // The summary used to be 9 hard-coded columns. As the catalog grew to
  // 152 keys / 19 categories, pinning columns no longer told a useful
  // story: half the catalog was invisible. The new design is a row-level
  // "category pip strip" — one pip per category, fill density encodes
  // how much of that category the role has. Scales as we add categories
  // without ever forcing a horizontal scroll.

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 16,
        }}
      >
        <div style={{ fontSize: 13, color: 'var(--tm)' }}>
          Define the roles and access privileges for your team. Each row shows a permission heatmap
          across {PERMISSION_CATEGORIES.length} categories. Hover any pip for detail.
        </div>
        <button onClick={() => setEditing(empty())} style={btnPrimary}>
          + Add Role
        </button>
      </div>

      {/* Legend strip - what the pip colors mean, shown once at the top */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          padding: '8px 12px',
          marginBottom: 8,
          background: 'var(--sa)',
          borderRadius: 6,
          fontSize: 11,
          color: 'var(--tm)',
        }}
      >
        <span style={{ fontWeight: 600 }}>Heatmap:</span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <PermPip fill="none" />
          <span>None</span>
        </span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <PermPip fill="partial" />
          <span>Partial</span>
        </span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <PermPip fill="full" />
          <span>All</span>
        </span>
      </div>

      {loading ? (
        <RowSkeleton label="Loading roles" />
      ) : (
        <div
          style={{
            background: 'var(--sf)',
            borderRadius: 8,
            border: '1px solid var(--bd)',
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(180px, 1fr) auto 60px',
              alignItems: 'center',
              gap: 16,
              padding: '10px 14px',
              background: 'var(--sa)',
              borderBottom: '1px solid var(--bd)',
              fontSize: 11,
              fontWeight: 700,
              color: 'var(--tm)',
              textTransform: 'uppercase',
              letterSpacing: '.04em',
            }}
          >
            <span>Role</span>
            <span style={{ textAlign: 'right' }}>
              Permissions <span style={{ fontWeight: 400, opacity: 0.7 }}>by category</span>
            </span>
            <span></span>
          </div>

          {/* Rows */}
          {roles.map((r) => {
            const rp = new Set(r.permissions || []);
            const totalEnabled = (r.permissions || []).filter((p) => p !== '*').length;
            const isWildcard = (r.permissions || []).includes('*');
            return (
              <div
                key={r.id}
                onClick={() => setEditing({ ...r })}
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'minmax(180px, 1fr) auto 60px',
                  alignItems: 'center',
                  gap: 16,
                  padding: '12px 14px',
                  borderBottom: '1px solid var(--bd)',
                  cursor: 'pointer',
                  transition: 'background .1s',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--sa)')}
                onMouseLeave={(e) => (e.currentTarget.style.background = '')}
              >
                {/* Name + count */}
                <div style={{ minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: 'var(--tx)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      flexWrap: 'wrap',
                    }}
                  >
                    <span>{r.name}</span>
                    {isRequired(r.name) && (
                      <span style={{ fontSize: 10, color: 'var(--tm)', fontWeight: 400 }}>
                        required
                      </span>
                    )}
                    {isWildcard && (
                      <span
                        style={{
                          fontSize: 10,
                          fontWeight: 700,
                          textTransform: 'uppercase',
                          letterSpacing: '.05em',
                          padding: '2px 6px',
                          borderRadius: 4,
                          background: 'rgba(var(--acr),0.15)',
                          color: 'var(--ac)',
                        }}
                      >
                        Site Admin
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--tm)', marginTop: 2 }}>
                    {isWildcard
                      ? 'All current and future permissions'
                      : `${Math.min(totalEnabled, ALL_PERMISSIONS.length)} of ${ALL_PERMISSIONS.length} permissions`}
                  </div>
                </div>

                {/* Pip strip */}
                <div
                  style={{
                    display: 'inline-flex',
                    gap: 3,
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    justifyContent: 'flex-end',
                    maxWidth: 480,
                  }}
                >
                  {PERMISSION_CATEGORIES.map((cat) => {
                    const enabled = isWildcard
                      ? cat.permissions.length
                      : cat.permissions.filter((p) => rp.has(p.key)).length;
                    const total = cat.permissions.length;
                    const fill: 'none' | 'partial' | 'full' =
                      enabled === 0 ? 'none' : enabled >= total ? 'full' : 'partial';
                    return (
                      <PermPip
                        key={cat.id}
                        fill={fill}
                        title={`${cat.label}: ${enabled} of ${total}`}
                      />
                    );
                  })}
                </div>

                {/* Action */}
                <div style={{ textAlign: 'right' }}>
                  {!isRequired(r.name) && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        remove(r.id);
                      }}
                      style={btnDanger}
                      title="Delete role"
                      aria-label={`Delete role ${r.name}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              </div>
            );
          })}

          {roles.length === 0 && (
            <EmptyState
              title="No roles yet"
              subtitle='Click "Add Role" or default roles will auto-seed on first load.'
            />
          )}
        </div>
      )}
    </div>
  );
}

/** A single category pip in the role-list heatmap. Uses the app accent
 *  color so the strip reads as filled vs empty rather than introducing
 *  a third hue. */
function PermPip({ fill, title }: { fill: 'none' | 'partial' | 'full'; title?: string }) {
  const variant: Record<typeof fill, React.CSSProperties> = {
    none: {
      background: 'transparent',
      border: '1px solid var(--bd)',
    },
    partial: {
      background: 'color-mix(in srgb, var(--ac) 30%, transparent)',
      border: '1px solid color-mix(in srgb, var(--ac) 60%, transparent)',
    },
    full: {
      background: 'var(--ac)',
      border: '1px solid var(--ac)',
    },
  };
  return (
    <span
      title={title}
      style={{
        display: 'inline-block',
        width: 14,
        height: 18,
        borderRadius: 3,
        flexShrink: 0,
        cursor: title ? 'help' : 'default',
        ...variant[fill],
      }}
    />
  );
}

/* ─── Titles Tab (was "Billing Roles" pre-v2.9.0) ───
   The table is still called `billing_roles` in the schema — the rename
   is a UI relabel only.  Migration 015 absorbed company_roles into here
   as display_only=true rows with no default rate. */

function BillingRolesTab({ toast }: { toast: AdminPageProps['toast'] }) {
  const [billingRoles, setBillingRoles] = useState<BillingRole[]>([]);
  const [editing, setEditing] = useState<BillingRole | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    load();
  }, []);
  useTableSync(['billing_roles'], () => load());
  async function load() {
    try {
      const list = await AdminService.billingRoles.list();
      setBillingRoles(list.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)));
    } finally {
      setLoading(false);
    }
  }

  function empty(): BillingRole {
    return {
      id: '',
      name: '',
      abbreviation: '',
      color: TITLE_COLORS[billingRoles.length % TITLE_COLORS.length],
      defaultRate: undefined,
      displayOnly: false,
      sortOrder: billingRoles.length,
      createdAt: '',
      updatedAt: '',
    };
  }

  async function save() {
    if (!editing) return;
    if (!editing.name.trim()) {
      toast('Enter a title name', true);
      return;
    }
    try {
      await AdminService.billingRoles.save(editing);
      await load();
      setEditing(null);
      toast('Title saved');
    } catch (err: any) {
      toast('Save failed: ' + err.message, true);
    }
  }

  async function remove(id: string) {
    if (
      !confirm('Delete this title? Any team members assigned to it will have their title cleared.')
    )
      return;
    try {
      await AdminService.billingRoles.delete(id);
      if (editing?.id === id) setEditing(null);
      await load();
      toast('Title deleted');
    } catch (err: any) {
      toast('Delete failed: ' + err.message, true);
    }
  }

  // Hook MUST be called before the early return below — rules-of-hooks.
  const drag = useDragReorder(billingRoles, async (reordered) => {
    setBillingRoles(reordered);
    try {
      await Promise.all(
        reordered.map((it, i) => {
          if ((billingRoles.find((x) => x.id === it.id)?.sortOrder ?? -1) !== i) {
            return AdminService.billingRoles.save({ ...it, sortOrder: i });
          }
          return Promise.resolve();
        })
      );
    } catch (err: any) {
      toast('Reorder failed: ' + err.message, true);
      await load();
    }
  });

  if (editing) {
    return (
      <div style={{ maxWidth: 580 }}>
        <button
          onClick={() => setEditing(null)}
          style={{
            ...btnDanger,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            marginBottom: 12,
            color: 'var(--tm)',
            border: '1px solid var(--bd)',
          }}
        >
          <ArrowLeft size={13} /> Back
        </button>
        <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 16 }}>
          {editing.id ? 'Edit Title' : 'New Title'}
        </h3>

        <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
          <label style={{ flex: 1 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--tm)', marginBottom: 4 }}>
              Title
            </div>
            <input
              value={editing.name}
              onChange={(e) =>
                setEditing((prev) => (prev ? { ...prev, name: e.target.value } : prev))
              }
              style={inputStyle}
              placeholder="e.g. Senior Engineer, Project Manager, BIM Manager"
            />
          </label>
          <label style={{ width: 120 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--tm)', marginBottom: 4 }}>
              Abbreviation
            </div>
            <input
              value={editing.abbreviation || ''}
              onChange={(e) =>
                setEditing((prev) => (prev ? { ...prev, abbreviation: e.target.value } : prev))
              }
              style={{ ...inputStyle, textTransform: 'uppercase' }}
              maxLength={6}
              placeholder="SE"
            />
          </label>
        </div>

        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--tm)', marginBottom: 6 }}>
            Color
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {TITLE_COLORS.map((c) => (
              <button
                key={c}
                onClick={() => setEditing((prev) => (prev ? { ...prev, color: c } : prev))}
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 6,
                  border: editing.color === c ? '2px solid var(--tx)' : '2px solid transparent',
                  background: c,
                  cursor: 'pointer',
                  padding: 0,
                  boxShadow:
                    editing.color === c ? '0 0 0 2px var(--bg), 0 0 0 4px var(--tx)' : 'none',
                  transition: 'border-color .12s, box-shadow .12s',
                }}
                title={c}
              />
            ))}
          </div>
        </div>

        {/* Display-only toggle — when checked, Title is HR/directory only
            and is excluded from invoice rate-card dropdowns by default. */}
        <div
          style={{
            marginBottom: 16,
            padding: '10px 12px',
            background: 'var(--sa)',
            borderRadius: 6,
            display: 'flex',
            alignItems: 'flex-start',
            gap: 10,
            cursor: 'pointer',
          }}
          onClick={() =>
            setEditing((prev) => (prev ? { ...prev, displayOnly: !prev.displayOnly } : prev))
          }
          role="checkbox"
          aria-checked={editing.displayOnly}
          aria-label="Display-only title (no rate)"
        >
          <div
            style={{
              width: 18,
              height: 18,
              borderRadius: 4,
              flexShrink: 0,
              border: `2px solid ${editing.displayOnly ? 'var(--ac)' : 'var(--bd)'}`,
              background: editing.displayOnly ? 'var(--ac)' : 'transparent',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--color-text-inverse)',
              fontSize: 11,
              marginTop: 1,
            }}
          >
            {editing.displayOnly ? <Check size={11} strokeWidth={3} /> : ''}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--tx)' }}>
              Display-only title (no rate)
            </div>
            <div style={{ fontSize: 11, color: 'var(--tm)', marginTop: 2 }}>
              Use for HR/directory titles like "BIM Manager" or "Admin" that aren't normally billed
              to clients. The invoice generator hides display-only titles from rate-card dropdowns.
            </div>
          </div>
        </div>

        {!editing.displayOnly && (
          <label style={{ display: 'block', marginBottom: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--tm)', marginBottom: 4 }}>
              Default Rate ($/hr)
            </div>
            <input
              type="number"
              min="0"
              step="5"
              value={editing.defaultRate ?? ''}
              onChange={(e) =>
                setEditing((prev) =>
                  prev
                    ? {
                        ...prev,
                        defaultRate: e.target.value ? parseFloat(e.target.value) : undefined,
                      }
                    : prev
                )
              }
              style={{ ...inputStyle, width: 180, fontFamily: "'JetBrains Mono', monospace" }}
              placeholder="0"
            />
            <div style={{ fontSize: 11, color: 'var(--tm)', marginTop: 4 }}>
              Default rate inherited by team members assigned to this Title. Per-person overrides
              are still possible. This value is gated — only users with{' '}
              <code style={{ fontSize: 10 }}>team_members.view_details</code> can see it outside
              Admin Center.
            </div>
          </label>
        )}

        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={save} style={btnPrimary}>
            Save Title
          </button>
          {editing.id && (
            <button onClick={() => remove(editing.id)} style={btnDanger}>
              Delete
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 16,
        }}
      >
        <div style={{ fontSize: 13, color: 'var(--tm)' }}>
          Titles are how the app identifies what people do — "Senior Engineer", "Project Manager",
          "BIM Manager", "Admin". Each Title can carry a default billing rate (gated to users with
          rate-view permission). Display-only Titles are HR/directory only and don't appear in
          invoice rate-card pickers. Drag the handle to reorder.
        </div>
        <button onClick={() => setEditing(empty())} style={btnPrimary}>
          + Add Title
        </button>
      </div>
      {loading ? (
        <RowSkeleton label="Loading titles" />
      ) : (
        <div
          style={{
            background: 'var(--sf)',
            borderRadius: 8,
            border: '1px solid var(--bd)',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '40px 32px 110px 1fr 140px 60px',
              gap: 12,
              padding: '8px 12px',
              background: 'var(--sa)',
              borderBottom: '1px solid var(--bd)',
              fontSize: 11,
              fontWeight: 700,
              color: 'var(--tm)',
              textTransform: 'uppercase',
              letterSpacing: '.04em',
            }}
          >
            <span></span>
            <span></span>
            <span>Abbr.</span>
            <span>Title</span>
            <span>Default Rate</span>
            <span></span>
          </div>
          {billingRoles.map((br, i) => {
            const isDragging = drag.dragIdx === i;
            const isDropTarget = drag.overIdx === i && drag.dragIdx !== null && drag.dragIdx !== i;
            return (
              <div
                key={br.id}
                {...drag.rowProps(i)}
                onClick={() => setEditing({ ...br })}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '40px 32px 110px 1fr 140px 60px',
                  gap: 12,
                  alignItems: 'center',
                  padding: '8px 12px',
                  borderBottom: i < billingRoles.length - 1 ? '1px solid var(--bd)' : 'none',
                  background: isDragging
                    ? 'rgba(var(--acr),0.08)'
                    : isDropTarget
                      ? 'rgba(var(--acr),0.04)'
                      : undefined,
                  opacity: isDragging ? 0.5 : 1,
                  cursor: 'pointer',
                  transition: 'background .1s',
                }}
              >
                <span
                  {...drag.handleProps()}
                  onClick={(e) => e.stopPropagation()}
                  style={{ ...DRAG_HANDLE_STYLE, ...drag.handleProps().style }}
                  title="Drag to reorder"
                  aria-label="Drag to reorder"
                >
                  <GripVertical size={14} />
                </span>
                <span
                  style={{
                    width: 20,
                    height: 20,
                    borderRadius: 5,
                    background: br.color || 'var(--bd)',
                    flexShrink: 0,
                  }}
                />
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: 'var(--tx)',
                    fontFamily: "'JetBrains Mono', monospace",
                    letterSpacing: '.02em',
                  }}
                >
                  {br.abbreviation || '\u2014'}
                </span>
                <span style={{ fontWeight: 600, fontSize: 13 }}>
                  {br.name}
                  {br.displayOnly && (
                    <span
                      style={{
                        marginLeft: 8,
                        fontSize: 9,
                        fontWeight: 700,
                        padding: '2px 6px',
                        borderRadius: 4,
                        background: 'var(--sa)',
                        color: 'var(--tm)',
                        textTransform: 'uppercase',
                        letterSpacing: '.05em',
                      }}
                    >
                      Display only
                    </span>
                  )}
                </span>
                <span
                  style={{
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: 13,
                    color: 'var(--tx)',
                  }}
                >
                  {br.displayOnly
                    ? '\u2014'
                    : br.defaultRate != null
                      ? `$${br.defaultRate}/hr`
                      : '\u2013'}
                </span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    remove(br.id);
                  }}
                  style={btnDanger}
                  title="Delete title"
                  aria-label={`Delete title ${br.name}`}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            );
          })}
          {billingRoles.length === 0 && (
            <EmptyState
              title="No titles yet"
              subtitle='Add what people are called: "Senior Engineer", "Project Manager", "BIM Manager", "Admin", etc.'
            />
          )}
        </div>
      )}
    </div>
  );
}

/* ─── Simple Lookup Table Editor (Project Types, Fee Structures, Billing Codes) ─── */
function LookupTableTab({
  title,
  description,
  service,
  tableName,
  toast,
}: {
  title: string;
  description: string;
  service: {
    list: () => Promise<any[]>;
    save: (item: any) => Promise<any>;
    delete: (id: string) => Promise<void>;
  };
  /** Backing table name so we only refetch when the relevant table changed. */
  tableName: string;
  toast: AdminPageProps['toast'];
}) {
  const [items, setItems] = useState<any[]>([]);
  const [newName, setNewName] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    load();
  }, []);
  useTableSync([tableName], () => load());
  async function load() {
    try {
      const rows = await service.list();
      rows.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
      setItems(rows);
    } finally {
      setLoading(false);
    }
  }

  async function add() {
    if (!newName.trim()) return;
    try {
      await service.save({
        id: '',
        name: newName.trim(),
        sortOrder: items.length,
        createdAt: '',
        updatedAt: '',
      });
      setNewName('');
      await load();
      toast(`${title} added`);
    } catch (err: any) {
      toast('Save failed: ' + err.message, true);
    }
  }

  async function update(id: string) {
    if (!editingName.trim()) return;
    const item = items.find((i) => i.id === id);
    if (item) {
      try {
        await service.save({ ...item, name: editingName.trim() });
        setEditingId(null);
        await load();
        toast(`${title} updated`);
      } catch (err: any) {
        toast('Update failed: ' + err.message, true);
      }
    }
  }

  async function remove(id: string) {
    if (!confirm(`Delete this ${title.toLowerCase()}?`)) return;
    try {
      await service.delete(id);
      await load();
      toast(`${title} deleted`);
    } catch (err: any) {
      toast('Delete failed: ' + err.message, true);
    }
  }

  // Drag-to-reorder: rewrite sortOrder for every row whose index changed, then reload.
  const drag = useDragReorder(items, async (reordered) => {
    setItems(reordered); // Optimistic
    try {
      await Promise.all(
        reordered.map((it, i) => {
          if ((items.find((x) => x.id === it.id)?.sortOrder ?? -1) !== i) {
            return service.save({ ...it, sortOrder: i });
          }
          return Promise.resolve();
        })
      );
    } catch (err: any) {
      toast('Reorder failed: ' + err.message, true);
      await load(); // Rollback
    }
  });

  return (
    <div>
      <div style={{ fontSize: 13, color: 'var(--tm)', marginBottom: 16 }}>{description}</div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <input
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && add()}
          placeholder={`New ${title.toLowerCase()}...`}
          style={{ ...inputStyle, width: 300 }}
        />
        <button onClick={add} style={btnPrimary}>
          + Add
        </button>
      </div>

      <div
        style={{
          background: 'var(--sf)',
          borderRadius: 8,
          border: '1px solid var(--bd)',
          overflow: 'hidden',
        }}
      >
        {loading ? (
          <RowSkeleton label={`Loading ${title.toLowerCase()}`} />
        ) : items.length === 0 ? (
          <EmptyState
            title={`No ${title.toLowerCase()} defined`}
            subtitle="Defaults will be seeded automatically."
          />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {items.map((item, i) => {
              const isDragging = drag.dragIdx === i;
              const isDropTarget =
                drag.overIdx === i && drag.dragIdx !== null && drag.dragIdx !== i;
              return (
                <div
                  key={item.id}
                  {...drag.rowProps(i)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '8px 12px',
                    borderBottom: i < items.length - 1 ? '1px solid var(--bd)' : 'none',
                    borderTop:
                      isDropTarget && drag.dragIdx! > i ? '2px solid var(--ac)' : undefined,
                    background: isDragging
                      ? 'rgba(var(--acr),0.08)'
                      : isDropTarget
                        ? 'rgba(var(--acr),0.04)'
                        : undefined,
                    opacity: isDragging ? 0.5 : 1,
                    transition: 'background .1s',
                  }}
                >
                  <span
                    {...drag.handleProps()}
                    style={{ ...DRAG_HANDLE_STYLE, ...drag.handleProps().style }}
                    title="Drag to reorder"
                    aria-label="Drag to reorder"
                  >
                    <GripVertical size={14} />
                  </span>
                  {editingId === item.id ? (
                    <>
                      <input
                        value={editingName}
                        onChange={(e) => setEditingName(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && update(item.id)}
                        style={{ ...inputStyle, flex: 1 }}
                        autoFocus
                      />
                      <button
                        onClick={() => update(item.id)}
                        style={{ ...btnPrimary, padding: '5px 10px' }}
                      >
                        Save
                      </button>
                      <button
                        onClick={() => setEditingId(null)}
                        style={{ ...btnDanger, color: 'var(--tm)' }}
                      >
                        Cancel
                      </button>
                    </>
                  ) : (
                    <>
                      <span style={{ flex: 1, fontSize: 13 }}>{item.name}</span>
                      <button
                        onClick={() => {
                          setEditingId(item.id);
                          setEditingName(item.name);
                        }}
                        style={{ ...btnDanger, color: 'var(--tm)' }}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => remove(item.id)}
                        style={btnDanger}
                        title={`Delete ${title.toLowerCase()}`}
                        aria-label={`Delete ${item.name}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── CSV Import Tab ─── */
function ImportTab({ toast }: { toast: AdminPageProps['toast'] }) {
  const [importType, setImportType] = useState<'team' | 'projects' | 'clients'>('team');
  const [csvData, setCsvData] = useState<{
    headers: string[];
    rows: Record<string, string>[];
  } | null>(null);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [importing, setImporting] = useState(false);

  const fieldsByType: Record<string, string[]> = {
    team: ['name', 'email', 'role', 'hourlyRate', 'active'],
    projects: [
      'name',
      'projectNumber',
      'clientName',
      'projectType',
      'feeStructure',
      'billingCode',
      'status',
      'description',
    ],
    clients: [
      'companyName',
      'firstName',
      'lastName',
      'address',
      'city',
      'state',
      'zip',
      'rwbPoc',
      'notes',
    ],
  };
  const expectedByType: Record<string, string> = {
    team: 'Expected columns: Name, Email, Role, Hourly Rate, Active',
    projects:
      'Expected columns: Name, Project Number, Client, Project Type, Fee Structure, Billing Code, Status, Description',
    clients:
      'Expected: Company Name, First Name, Last Name, Address, City, State, Zip, RWB POC, Notes. Rows are grouped by Company Name.',
  };

  async function pickFile() {
    const filePath = await window.api.openCsvDialog();
    if (!filePath) return;
    const data = await window.api.parseCsvFile(filePath);
    setCsvData(data);
    const fields = fieldsByType[importType];
    const autoMap: Record<string, string> = {};
    // Aliases for common column name variations
    const aliases: Record<string, string[]> = {
      companyName: ['companyname', 'company', 'name', 'firm', 'client'],
      firstName: ['firstname', 'first', 'fname'],
      lastName: ['lastname', 'last', 'lname'],
      address: ['address', 'streetaddress', 'street', 'addr'],
      rwbPoc: ['rwbpoc', 'rwb', 'poc', 'principle'],
      notes: ['notes', 'note', 'comments'],
      clientName: ['clientname', 'client', 'clientorg', 'organization', 'org'],
      projectType: ['projecttype', 'type', 'projtype'],
      feeStructure: ['feestructure', 'billingtype', 'billing', 'billtype', 'fee'],
      billingCode: ['billingcode', 'activitycode', 'activity', 'description', 'actcode'],
    };
    for (const f of fields) {
      const norms = aliases[f] || [f.toLowerCase()];
      const match = data.headers.find((h) => {
        const hn = h.toLowerCase().replace(/[_\s-]/g, '');
        return norms.some((n) => hn === n);
      });
      if (match) autoMap[f] = match;
    }
    setMapping(autoMap);
  }

  async function doImport() {
    if (!csvData) return;
    setImporting(true);
    let count = 0;
    try {
      if (importType === 'team') {
        for (const row of csvData.rows) {
          const name = row[mapping['name']] || '';
          if (!name.trim()) continue;
          await TeamService.save({
            id: '',
            name: name.trim(),
            email: row[mapping['email']] || '',
            role: row[mapping['role']] || '',
            hourlyRate:
              mapping['hourlyRate'] && row[mapping['hourlyRate']]
                ? parseFloat(row[mapping['hourlyRate']])
                : undefined,
            active: mapping['active']
              ? (row[mapping['active']] || '').toLowerCase() !== 'false' &&
                row[mapping['active']] !== '0'
              : true,
            createdAt: '',
            updatedAt: '',
          });
          count++;
        }
      } else if (importType === 'projects') {
        // Pre-load lookup tables for matching by name
        const [allOrgs, allPTypes, allBTypes, allACodes] = await Promise.all([
          OrgService.list(),
          AdminService.projectTypes.list(),
          AdminService.billingTypes.list(),
          AdminService.activityCodes.list(),
        ]);
        const findOrg = (name: string) => {
          if (!name) return null;
          const n = name.toLowerCase().trim();
          return allOrgs.find((o) => o.name.toLowerCase() === n)?.id || null;
        };
        const findLookup = (list: { id: string; name: string }[], name: string) => {
          if (!name) return null;
          const n = name.toLowerCase().trim();
          return list.find((x) => x.name.toLowerCase() === n)?.id || null;
        };

        for (const row of csvData.rows) {
          const name = row[mapping['name']] || '';
          if (!name.trim()) continue;
          const status = (row[mapping['status']] || '').toLowerCase();
          const clientStr = (row[mapping['clientName']] || '').trim();
          const clientOrgId = findOrg(clientStr);
          const projectTypeId = findLookup(allPTypes, (row[mapping['projectType']] || '').trim());
          const billingTypeId = findLookup(allBTypes, (row[mapping['feeStructure']] || '').trim());
          const activityCodeId = findLookup(allACodes, (row[mapping['billingCode']] || '').trim());
          await ProjectService.save({
            id: '',
            name: name.trim(),
            projectNumber: row[mapping['projectNumber']] || undefined,
            clientOrgId: clientOrgId || undefined,
            clientName: clientStr || undefined,
            projectTypeId: projectTypeId || undefined,
            billingTypeId: billingTypeId || undefined,
            activityCodeId: activityCodeId || undefined,
            status: status === 'closed' ? 'closed' : 'active',
            description: row[mapping['description']] || undefined,
            selections: {},
            createdAt: '',
            updatedAt: '',
          });
          count++;
        }
      } else if (importType === 'clients') {
        // Group rows by company name
        const grouped = new Map<string, typeof csvData.rows>();
        for (const row of csvData.rows) {
          const company = (row[mapping['companyName']] || '').trim();
          if (!company) continue;
          if (!grouped.has(company)) grouped.set(company, []);
          grouped.get(company)!.push(row);
        }
        for (const [company, rows] of grouped) {
          // Collect contacts from all rows
          const contacts: { name: string; title?: string; email?: string; phone?: string }[] = [];
          for (const row of rows) {
            const first = (row[mapping['firstName']] || '').trim();
            const last = (row[mapping['lastName']] || '').trim();
            if (first || last) contacts.push({ name: [first, last].filter(Boolean).join(' ') });
          }
          // Dedupe addresses
          const addrSet = new Set<string>();
          const addresses: {
            label?: string;
            address: string;
            city: string;
            state: string;
            zip: string;
          }[] = [];
          for (const row of rows) {
            const street = (row[mapping['address']] || '').trim();
            const city = (row[mapping['city']] || '').trim();
            const state = (row[mapping['state']] || '').trim();
            const zip = (row[mapping['zip']] || '').trim();
            if (!street && !city) continue;
            const key = `${street}|${city}|${state}|${zip}`;
            if (addrSet.has(key)) continue;
            addrSet.add(key);
            addresses.push({ address: street, city, state, zip });
          }
          // RWB POC + notes from first row that has them
          const rwbPoc =
            rows.find((r) => (r[mapping['rwbPoc']] || '').trim())?.[mapping['rwbPoc']]?.trim() ||
            '';
          const notes =
            rows.find((r) => (r[mapping['notes']] || '').trim())?.[mapping['notes']]?.trim() || '';
          await OrgService.save({
            id: '',
            name: company,
            type: 'client',
            addresses,
            contacts,
            rwbPoc,
            notes,
            createdAt: '',
            updatedAt: '',
          });
          count++;
        }
      }
      const labels: Record<string, string> = {
        team: 'team members',
        projects: 'projects',
        clients: 'clients',
      };
      toast(`Imported ${count} ${labels[importType]}`);
      setCsvData(null);
      setMapping({});
    } catch (err: any) {
      toast('Import error: ' + err.message, true);
    }
    setImporting(false);
  }

  const fields = fieldsByType[importType];
  const importLabels: Record<string, string> = {
    team: 'Members',
    projects: 'Projects',
    clients: 'Clients',
  };

  return (
    <div>
      <div style={{ fontSize: 13, color: 'var(--tm)', marginBottom: 16 }}>
        Import team members, projects, or clients from a CSV file. Great for migrating from Ajera or
        other systems.
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'center' }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--tm)' }}>Import:</div>
        <div
          style={{ display: 'flex', gap: 2, background: 'var(--sa)', borderRadius: 6, padding: 2 }}
        >
          {(['team', 'projects', 'clients'] as const).map((t) => (
            <button
              key={t}
              onClick={() => {
                setImportType(t);
                setCsvData(null);
              }}
              style={{
                padding: '5px 14px',
                borderRadius: 4,
                border: 'none',
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
                background: importType === t ? 'var(--sf)' : 'transparent',
                color: importType === t ? 'var(--tx)' : 'var(--tm)',
                boxShadow: importType === t ? 'var(--shadow-sm)' : 'none',
              }}
            >
              {t === 'team' ? 'Team Members' : t === 'projects' ? 'Projects' : 'Clients'}
            </button>
          ))}
        </div>
      </div>

      {!csvData ? (
        <div
          style={{
            textAlign: 'center',
            padding: '40px 20px',
            background: 'var(--sf)',
            borderRadius: 10,
            border: '2px dashed var(--bd)',
            cursor: 'pointer',
          }}
          onClick={pickFile}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              opacity: 0.3,
              marginBottom: 8,
              color: 'var(--tm)',
            }}
            aria-hidden
          >
            <Upload size={32} strokeWidth={1.5} />
          </div>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>
            Click to select a CSV file
          </div>
          <div style={{ fontSize: 12, color: 'var(--tm)' }}>{expectedByType[importType]}</div>
        </div>
      ) : (
        <div>
          <div
            style={{
              marginBottom: 16,
              padding: 12,
              background: 'var(--sf)',
              borderRadius: 8,
              border: '1px solid var(--bd)',
            }}
          >
            <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 8 }}>
              Found {csvData.rows.length} rows with {csvData.headers.length} columns
            </div>
            <div style={{ fontSize: 11, color: 'var(--tm)', marginBottom: 12 }}>
              Map your CSV columns to SpecForma fields:
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              {fields.map((f) => (
                <div key={f} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 12, fontWeight: 600, width: 100 }}>{f}:</span>
                  <div style={{ flex: 1 }}>
                    <SearchableSelect
                      value={mapping[f] || ''}
                      options={csvData.headers.map((h) => ({ value: h, label: h }))}
                      onChange={(v) => setMapping((prev) => ({ ...prev, [f]: v }))}
                      placeholder="-- skip --"
                      searchPlaceholder="Search columns…"
                      style={{ ...inputStyle, fontSize: 12 }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div
            style={{
              marginBottom: 16,
              maxHeight: 200,
              overflow: 'auto',
              background: 'var(--sf)',
              borderRadius: 8,
              border: '1px solid var(--bd)',
            }}
          >
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
              <thead>
                <tr>
                  {csvData.headers.map((h) => (
                    <th key={h} style={thStyle}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {csvData.rows.slice(0, 5).map((row, i) => (
                  <tr key={i}>
                    {csvData.headers.map((h) => (
                      <td key={h} style={tdStyle}>
                        {row[h]}
                      </td>
                    ))}
                  </tr>
                ))}
                {csvData.rows.length > 5 && (
                  <tr>
                    <td
                      colSpan={csvData.headers.length}
                      style={{ ...tdStyle, textAlign: 'center', color: 'var(--tm)' }}
                    >
                      ... and {csvData.rows.length - 5} more rows
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={doImport}
              disabled={importing || !(mapping['name'] || mapping['companyName'])}
              style={{
                ...btnPrimary,
                opacity: importing || !(mapping['name'] || mapping['companyName']) ? 0.5 : 1,
              }}
            >
              {importing
                ? 'Importing...'
                : `Import ${importType === 'clients' ? new Set(csvData.rows.map((r) => (r[mapping['companyName']] || '').trim()).filter(Boolean)).size + ' Clients' : csvData.rows.length + ' ' + importLabels[importType]}`}
            </button>
            <button
              onClick={() => {
                setCsvData(null);
                setMapping({});
              }}
              style={{ ...btnDanger, color: 'var(--tm)', border: '1px solid var(--bd)' }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ═══════════ SUBSCRIPTION TAB ═══════════
   Reads + writes the `firm_modules` table directly via cloudCache. Each
   row is { id, firmId, module, enabled }. Toggling re-uses the existing
   row's id when one exists (so we update instead of accumulating dupes);
   first-time enables create a new row.
   The toggle is gated by `admin_center.manage_subscription` — users
   without it see the same cards as read-only badges.
   ═══════════════════════════════════════════════════════════════ */
interface FirmModuleRow {
  id: string;
  firmId?: string;
  module: AppModule;
  enabled: boolean;
}

function SubscriptionTab({
  toast,
  userPermissions,
}: {
  toast: AdminPageProps['toast'];
  userPermissions?: string[];
}) {
  const [rows, setRows] = useState<FirmModuleRow[]>([]);
  const [savingModule, setSavingModule] = useState<AppModule | null>(null);
  const [loading, setLoading] = useState(true);
  const canEdit = hasPermission(userPermissions, 'admin_center.manage_subscription');

  async function load() {
    try {
      const list = await cloudCache.list<FirmModuleRow>('firm_modules');
      setRows(list);
    } catch (err: any) {
      console.error('[SubscriptionTab] load failed:', err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);
  useTableSync(['firm_modules'], () => load());

  async function toggle(mod: AppModule, nextEnabled: boolean) {
    if (!canEdit || savingModule) return;
    setSavingModule(mod);
    try {
      const existing = rows.find((r) => r.module === mod);
      const record: FirmModuleRow = existing
        ? { ...existing, enabled: nextEnabled }
        : { id: '', module: mod, enabled: nextEnabled };
      const saved = await cloudCache.save('firm_modules', record);
      // Optimistic refresh — cloudCache already updated its cache, but pull
      // local state so the row id from a fresh insert is captured.
      setRows((prev) => {
        const idx = prev.findIndex((r) => r.module === mod);
        if (idx >= 0) {
          const next = [...prev];
          next[idx] = saved;
          return next;
        }
        return [...prev, saved];
      });
      toast(`${MODULE_DEFS[mod].name} ${nextEnabled ? 'enabled' : 'disabled'}`);
    } catch (err: any) {
      toast('Update failed: ' + (err?.message || String(err)), true);
      await load();
    } finally {
      setSavingModule(null);
    }
  }

  const allModules = Object.entries(MODULE_DEFS).filter(([id]) => id !== 'core') as [
    AppModule,
    (typeof MODULE_DEFS)[AppModule],
  ][];

  return (
    <div style={{ maxWidth: 720 }}>
      <h3 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>Subscription</h3>
      <p style={{ fontSize: 12, color: 'var(--tm)', margin: '4px 0 16px' }}>
        {canEdit
          ? 'Toggle which modules your firm has access to. Disabling a module hides its pages and blocks its API endpoints for everyone in the firm.'
          : 'Modules currently enabled for your firm. Ask an administrator to change your subscription.'}
      </p>

      {loading ? (
        <RowSkeleton rows={3} label="Loading subscription modules" />
      ) : (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
          }}
        >
          {allModules.map(([id, def]) => {
            const row = rows.find((r) => r.module === id);
            const enabled = !!row?.enabled;
            const isSaving = savingModule === id;
            return (
              <div
                key={id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 14,
                  padding: '14px 16px',
                  background: 'var(--sf)',
                  borderRadius: 10,
                  border: `1px solid ${enabled ? 'var(--ac)' : 'var(--bd)'}`,
                  transition: 'border-color .15s, background .15s',
                  opacity: isSaving ? 0.7 : 1,
                }}
              >
                <div
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 8,
                    background: enabled ? 'var(--ac)' : 'var(--sa)',
                    color: enabled ? 'var(--color-text-inverse)' : 'var(--tm)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 16,
                    fontWeight: 700,
                    flexShrink: 0,
                    transition: 'background .15s, color .15s',
                  }}
                >
                  {def.name.charAt(0)}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 14,
                      fontWeight: 700,
                      color: 'var(--tx)',
                      marginBottom: 2,
                    }}
                  >
                    {def.name}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--tm)', lineHeight: 1.4 }}>
                    {def.description}
                  </div>
                </div>
                {canEdit ? (
                  <Switch
                    checked={enabled}
                    disabled={isSaving}
                    onCheckedChange={(next) => toggle(id, next)}
                    aria-label={`${enabled ? 'Disable' : 'Enable'} ${def.name}`}
                  />
                ) : (
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: '.05em',
                      padding: '4px 10px',
                      borderRadius: 999,
                      background: enabled ? 'rgba(var(--okr),0.12)' : 'var(--sa)',
                      color: enabled ? 'var(--ok)' : 'var(--tm)',
                    }}
                  >
                    {enabled ? 'Active' : 'Inactive'}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}

      {canEdit && (
        <p
          style={{
            fontSize: 11,
            color: 'var(--tm)',
            marginTop: 16,
            padding: '10px 12px',
            background: 'var(--sa)',
            borderRadius: 6,
            lineHeight: 1.5,
          }}
        >
          <strong>Note:</strong> Module changes take effect on next sign-in for other users in your
          firm. Core (Dashboard, Settings, Admin) is always enabled and cannot be turned off.
        </p>
      )}
    </div>
  );
}

/* ═══════════ MAIN ADMIN PAGE ═══════════ */
function AdminPage({ toast, userPermissions, userEmail }: AdminPageProps) {
  // Filter groups to only those the user has access to
  let visibleGroups = TAB_GROUPS.filter(
    (g) => !g.permission || hasPermission(userPermissions, g.permission)
  );
  // HAR Sync (RWB bridge) is account-gated, not permission-gated — it only
  // exists for Josh's account on desktop (canHarSync checks both).
  if (canHarSync({ email: userEmail })) {
    visibleGroups = visibleGroups.map((g) =>
      g.label === 'Data'
        ? { ...g, tabs: [...g.tabs, { id: 'harSync' as Tab, label: 'HAR Sync' }] }
        : g
    );
  }
  const allVisibleTabs = visibleGroups.flatMap((g) => g.tabs);
  const [tab, setTab] = useState<Tab>(allVisibleTabs[0]?.id || 'roles');

  // If current tab is no longer visible (permissions changed), reset to first visible
  const currentTabVisible = allVisibleTabs.some((t) => t.id === tab);
  if (!currentTabVisible && allVisibleTabs.length > 0 && tab !== allVisibleTabs[0].id) {
    setTab(allVisibleTabs[0].id);
  }

  return (
    <div style={{ padding: 24, overflow: 'auto', height: '100%' }}>
      <div style={{ marginBottom: 20 }}>
        <h2 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>Admin Center</h2>
        <p style={{ fontSize: 13, color: 'var(--tm)', margin: '4px 0 0' }}>
          Manage roles, rates, billing codes, fee structures, and import data
        </p>
      </div>

      {/* Grouped tab bar */}
      <div
        style={{
          display: 'flex',
          gap: 12,
          marginBottom: 20,
          flexWrap: 'wrap',
          alignItems: 'center',
        }}
      >
        {visibleGroups.map((group, gi) => (
          <React.Fragment key={group.label}>
            {gi > 0 && (
              <div style={{ width: 1, height: 24, background: 'var(--bd)', flexShrink: 0 }} />
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '.06em',
                  color: 'var(--tm)',
                  padding: '0 4px',
                }}
              >
                {group.label}
              </span>
              <div
                style={{
                  display: 'flex',
                  gap: 2,
                  background: 'var(--sa)',
                  borderRadius: 6,
                  padding: 2,
                }}
              >
                {group.tabs.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => setTab(t.id)}
                    style={{
                      padding: '6px 14px',
                      borderRadius: 4,
                      border: 'none',
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: 'pointer',
                      background: tab === t.id ? 'var(--sf)' : 'transparent',
                      color: tab === t.id ? 'var(--tx)' : 'var(--tm)',
                      boxShadow: tab === t.id ? 'var(--shadow-sm)' : 'none',
                      transition: 'all .15s',
                    }}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
          </React.Fragment>
        ))}
      </div>

      {/* Tab content */}
      {tab === 'roles' && <RolesTab toast={toast} />}
      {tab === 'billingRoles' && <BillingRolesTab toast={toast} />}
      {tab === 'projectTypes' && (
        <LookupTableTab
          key="pt"
          title="Project Type"
          description="Categorize projects by type (e.g., Educational K-12, Municipal, Medical)."
          service={AdminService.projectTypes}
          tableName="project_types"
          toast={toast}
        />
      )}
      {tab === 'billingTypes' && (
        <LookupTableTab
          key="bt"
          title="Fee Structure"
          description="Define fee structures (e.g., Percent of Construction Cost, Time & Expense, Fixed Fee)."
          service={AdminService.billingTypes}
          tableName="billing_types"
          toast={toast}
        />
      )}
      {tab === 'activityCodes' && (
        <LookupTableTab
          key="ac"
          title="Billing Code"
          description="Billing codes for time tracking (e.g., HVAC, Electrical, Specifications)."
          service={AdminService.activityCodes}
          tableName="activity_codes"
          toast={toast}
        />
      )}
      {tab === 'phases' && (
        <LookupTableTab
          key="ph"
          title="Phase"
          description="Universal project phases. All projects share these phases (e.g., Schematic Design, Construction Documents). Change the order or add new phases here."
          service={AdminService.defaultPhases}
          tableName="default_phases"
          toast={toast}
        />
      )}
      {tab === 'overheadCategories' && (
        <LookupTableTab
          key="oc"
          title="Overhead Category"
          description="Non-billable overhead categories for timesheets (e.g., Holiday, PTO, Marketing)."
          service={AdminService.overheadCategories}
          tableName="overhead_categories"
          toast={toast}
        />
      )}
      {tab === 'vacationCategories' && (
        <LookupTableTab
          key="vc"
          title="Vacation Category"
          description="Leave types for vacation requests (e.g., PTO, Vacation, Sick, Personal). Employees select these when submitting time-off requests."
          service={VacationService.categories}
          tableName="vacation_categories"
          toast={toast}
        />
      )}
      {tab === 'firmHolidays' && <FirmHolidaysTab toast={toast} />}
      {tab === 'expenseTypes' && <ExpenseTypesTab toast={toast} />}
      {tab === 'scopes' && <ScopesTab toast={toast} />}
      {tab === 'departments' && <DepartmentsTab toast={toast} />}
      {tab === 'import' && <ImportTab toast={toast} />}
      {tab === 'harSync' && <HarSyncTab toast={toast} />}
      {tab === 'subscription' && (
        <SubscriptionTab toast={toast} userPermissions={userPermissions} />
      )}
      {tab === 'activity' && <ActivityLogTab toast={toast} />}
    </div>
  );
}

/* ═══════════ ACTIVITY LOG TAB ═══════════
   Cross-user, server-side audit log of every meaningful firm action.
   Used by principals to spot dormant beta testers and watch usage.
   30-day retention; pruning runs in the background on EC2. */
function ActivityLogTab({ toast }: { toast: AdminPageProps['toast'] }) {
  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <p style={{ fontSize: 13, color: 'var(--tm)', margin: 0 }}>
          Server-side audit log of every meaningful action across the firm. 30-day retention. Use
          this to spot dormant beta testers and watch usage patterns.
        </p>
      </div>
      <FirmActivityPanel toast={toast} />
    </div>
  );
}

/* ═══════════ DEPARTMENTS TAB ═══════════
   Department lookup with name + abbreviation + color picker.
   Drag-orderable like the other lookups; the order set here drives
   forecast-grid grouping order and team-page sort. */
function DepartmentsTab({ toast }: { toast: AdminPageProps['toast'] }) {
  const [items, setItems] = useState<Department[]>([]);
  const [editing, setEditing] = useState<Department | null>(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  async function load() {
    try {
      const list = await DepartmentService.list();
      setItems(list);
    } catch (err: any) {
      toast('Load failed: ' + err.message, true);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    load();
  }, []);
  useTableSync(['departments'], () => load());

  function empty(): Department {
    return {
      id: '',
      name: '',
      abbreviation: '',
      color: DEPARTMENT_COLORS[items.length % DEPARTMENT_COLORS.length],
      sortOrder: items.length,
    };
  }

  async function save() {
    if (!editing || saving) return;
    if (!editing.name.trim()) {
      toast('Enter a department name', true);
      return;
    }
    setSaving(true);
    try {
      await DepartmentService.save(editing);
      setEditing(null);
      await load();
      toast('Department saved');
    } catch (err: any) {
      toast('Save failed: ' + err.message, true);
    } finally {
      setSaving(false);
    }
  }

  async function remove(id: string) {
    if (
      !confirm('Delete this department? Members assigned to it will have their department cleared.')
    )
      return;
    try {
      await DepartmentService.delete(id);
      if (editing?.id === id) setEditing(null);
      await load();
      toast('Department deleted');
    } catch (err: any) {
      toast('Delete failed: ' + err.message, true);
    }
  }

  const drag = useDragReorder(items, async (reordered) => {
    setItems(reordered);
    try {
      await Promise.all(
        reordered.map((it, i) => {
          if ((items.find((x) => x.id === it.id)?.sortOrder ?? -1) !== i) {
            return DepartmentService.save({ ...it, sortOrder: i });
          }
          return Promise.resolve();
        })
      );
    } catch (err: any) {
      toast('Reorder failed: ' + err.message, true);
      await load();
    }
  });

  if (editing) {
    return (
      <div style={{ maxWidth: 540 }}>
        <button
          onClick={() => setEditing(null)}
          style={{
            ...btnDanger,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            marginBottom: 12,
            color: 'var(--tm)',
            border: '1px solid var(--bd)',
          }}
        >
          <ArrowLeft size={13} /> Back
        </button>
        <h3 style={{ fontSize: 15, fontWeight: 700, margin: '0 0 16px' }}>
          {editing.id ? 'Edit Department' : 'New Department'}
        </h3>

        <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
          <label style={{ flex: 1 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--tm)', marginBottom: 4 }}>
              Department Name
            </div>
            <input
              value={editing.name}
              onChange={(e) => setEditing({ ...editing, name: e.target.value })}
              style={inputStyle}
              placeholder="e.g. Mechanical, Electrical, Plumbing & Tech"
            />
          </label>
          <label style={{ width: 120 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--tm)', marginBottom: 4 }}>
              Abbreviation
            </div>
            <input
              value={editing.abbreviation || ''}
              onChange={(e) => setEditing({ ...editing, abbreviation: e.target.value })}
              style={{ ...inputStyle, textTransform: 'uppercase' }}
              maxLength={6}
              placeholder="MEPT"
            />
          </label>
        </div>

        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--tm)', marginBottom: 6 }}>
            Color
          </div>
          {/* Quick-pick palette + native color picker + hex input. The palette
              is for fast common picks; the picker + hex are for the user who
              has a brand hex memorized or wants to match a logo. All three
              control the same `editing.color` state. */}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
            {DEPARTMENT_COLORS.map((c) => (
              <button
                key={c}
                onClick={() => setEditing({ ...editing, color: c })}
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 6,
                  border: editing.color === c ? '2px solid var(--tx)' : '2px solid transparent',
                  background: c,
                  cursor: 'pointer',
                  padding: 0,
                  boxShadow:
                    editing.color === c ? '0 0 0 2px var(--bg), 0 0 0 4px var(--tx)' : 'none',
                  transition: 'border-color .12s, box-shadow .12s',
                }}
                title={c}
                aria-label={`Color ${c}`}
              />
            ))}
            <span
              aria-hidden="true"
              style={{
                width: 1,
                height: 20,
                background: 'var(--bd)',
                marginInline: 4,
              }}
            />
            <label
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                cursor: 'pointer',
              }}
              title="Custom color"
            >
              <input
                type="color"
                value={editing.color || '#888888'}
                onChange={(e) => setEditing({ ...editing, color: e.target.value })}
                style={{
                  width: 32,
                  height: 28,
                  padding: 0,
                  border: '1px solid var(--bd)',
                  borderRadius: 6,
                  cursor: 'pointer',
                  background: 'transparent',
                }}
                aria-label="Pick a custom color"
              />
              <span style={{ fontSize: 11, color: 'var(--tm)' }}>Custom</span>
            </label>
            <input
              type="text"
              value={editing.color || ''}
              onChange={(e) => {
                const v = e.target.value.trim();
                // Accept #abc, #abcdef, abc, abcdef. Light validation only —
                // browser color input handles canonicalization on commit.
                if (!v || /^#?[0-9a-fA-F]{3}([0-9a-fA-F]{3})?$/.test(v)) {
                  const normalized = v ? (v.startsWith('#') ? v : '#' + v) : '';
                  setEditing({ ...editing, color: normalized });
                }
              }}
              placeholder="#5b9ad6"
              maxLength={7}
              style={{
                width: 100,
                padding: '5px 8px',
                borderRadius: 5,
                border: '1px solid var(--bd)',
                background: 'var(--ib)',
                color: 'var(--tx)',
                fontSize: 12,
                fontFamily: "'JetBrains Mono', monospace",
                textTransform: 'lowercase',
              }}
              aria-label="Color hex value"
            />
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={save}
            disabled={saving}
            style={{ ...btnPrimary, opacity: saving ? 0.6 : 1 }}
          >
            {saving ? 'Saving…' : 'Save Department'}
          </button>
          {editing.id && (
            <button onClick={() => remove(editing.id)} style={btnDanger}>
              Delete
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 16,
        }}
      >
        <div style={{ fontSize: 13, color: 'var(--tm)' }}>
          Departments group people on the team page and the forecasting grid (e.g. MEPT, BIM,
          Architecture). Drag rows to reorder.
        </div>
        <button onClick={() => setEditing(empty())} style={btnPrimary}>
          + Add Department
        </button>
      </div>

      {loading ? (
        <RowSkeleton label="Loading departments" />
      ) : (
        <div
          style={{
            background: 'var(--sf)',
            borderRadius: 8,
            border: '1px solid var(--bd)',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '40px 32px 110px 1fr 80px',
              gap: 12,
              alignItems: 'center',
              padding: '8px 12px',
              background: 'var(--sa)',
              borderBottom: '1px solid var(--bd)',
              fontSize: 11,
              fontWeight: 700,
              color: 'var(--tm)',
              textTransform: 'uppercase',
              letterSpacing: '.04em',
            }}
          >
            <span></span>
            <span></span>
            <span>Abbreviation</span>
            <span>Department</span>
            <span></span>
          </div>

          {items.map((d, i) => {
            const isDragging = drag.dragIdx === i;
            const isDropTarget = drag.overIdx === i && drag.dragIdx !== null && drag.dragIdx !== i;
            return (
              <div
                key={d.id}
                {...drag.rowProps(i)}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '40px 32px 110px 1fr 80px',
                  gap: 12,
                  alignItems: 'center',
                  padding: '10px 12px',
                  borderBottom: '1px solid var(--bd)',
                  background: isDragging
                    ? 'var(--sa)'
                    : isDropTarget
                      ? 'rgba(var(--acr),0.08)'
                      : '',
                  opacity: isDragging ? 0.5 : 1,
                  transition: 'background .1s',
                  cursor: 'pointer',
                }}
                onClick={() => setEditing({ ...d })}
              >
                <span
                  {...drag.handleProps()}
                  style={DRAG_HANDLE_STYLE}
                  title="Drag to reorder"
                  aria-label="Drag to reorder"
                >
                  <GripVertical size={14} />
                </span>
                <span
                  style={{
                    width: 20,
                    height: 20,
                    borderRadius: 5,
                    background: d.color || 'var(--bd)',
                    flexShrink: 0,
                  }}
                />
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: 'var(--tx)',
                    fontFamily: "'JetBrains Mono', monospace",
                    letterSpacing: '.02em',
                  }}
                >
                  {d.abbreviation || '—'}
                </span>
                <span style={{ fontSize: 13, color: 'var(--tx)' }}>{d.name}</span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    remove(d.id);
                  }}
                  style={btnDanger}
                  title="Delete department"
                  aria-label={`Delete department ${d.name}`}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            );
          })}

          {items.length === 0 && (
            <EmptyState
              title="No departments yet"
              subtitle='Click "Add Department" to create the first.'
            />
          )}
        </div>
      )}
    </div>
  );
}

/* ─── Project Scopes Tab ─── */
function ScopesTab({ toast }: { toast: AdminPageProps['toast'] }) {
  const [scopes, setScopes] = useState<ProjectScope[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [editingColor, setEditingColor] = useState('#6c757d');
  const [addingParent, setAddingParent] = useState(false);
  const [addingChildOf, setAddingChildOf] = useState<string | null>(null);
  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState('#2563eb');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    load();
  }, []);
  useTableSync(['project_scopes'], () => load());

  async function load() {
    try {
      setScopes(await AdminService.projectScopes.list());
    } finally {
      setLoading(false);
    }
  }

  const parentScopes = scopes.filter((s) => !s.parentId);
  const childScopes = (parentId: string) => scopes.filter((s) => s.parentId === parentId);

  async function addScope(parentId?: string) {
    if (!newName.trim()) return;
    try {
      await AdminService.projectScopes.save({
        id: '',
        name: newName.trim(),
        parentId: parentId || undefined,
        color: newColor,
        sortOrder: scopes.length,
        createdAt: '',
        updatedAt: '',
      });
      setNewName('');
      setNewColor(DEFAULT_SCOPE_COLORS[scopes.length % DEFAULT_SCOPE_COLORS.length] || '#2563eb');
      setAddingParent(false);
      setAddingChildOf(null);
      await load();
      toast('Scope added');
    } catch (err: any) {
      toast('Save failed: ' + err.message, true);
    }
  }

  async function updateScope(id: string) {
    if (!editingName.trim()) return;
    const scope = scopes.find((s) => s.id === id);
    if (scope) {
      try {
        await AdminService.projectScopes.save({
          ...scope,
          name: editingName.trim(),
          color: editingColor,
        });
        setEditingId(null);
        await load();
        toast('Scope updated');
      } catch (err: any) {
        toast('Update failed: ' + err.message, true);
      }
    }
  }

  async function removeScope(id: string) {
    const scope = scopes.find((s) => s.id === id);
    const children = childScopes(id);
    const msg =
      children.length > 0
        ? `Delete "${scope?.name}" and its ${children.length} sub-scope${children.length !== 1 ? 's' : ''}?`
        : `Delete "${scope?.name}"?`;
    if (!confirm(msg)) return;
    try {
      await AdminService.projectScopes.delete(id);
      if (editingId === id) setEditingId(null);
      await load();
      toast('Scope deleted');
    } catch (err: any) {
      toast('Delete failed: ' + err.message, true);
    }
  }

  function startEdit(scope: ProjectScope) {
    setEditingId(scope.id);
    setEditingName(scope.name);
    setEditingColor(scope.color);
  }

  function renderScopeRow(scope: ProjectScope, indent: boolean) {
    const isEditing = editingId === scope.id;
    return (
      <div
        key={scope.id}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '8px 12px',
          paddingLeft: indent ? 40 : 12,
          borderBottom: '1px solid var(--bd)',
        }}
      >
        {isEditing ? (
          <>
            <input
              type="color"
              value={editingColor}
              onChange={(e) => setEditingColor(e.target.value)}
              style={{
                width: 28,
                height: 28,
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                padding: 0,
              }}
            />
            <input
              value={editingName}
              onChange={(e) => setEditingName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && updateScope(scope.id)}
              style={{ ...inputStyle, flex: 1 }}
              autoFocus
            />
            <button
              onClick={() => updateScope(scope.id)}
              style={{ ...btnPrimary, padding: '5px 10px' }}
            >
              Save
            </button>
            <button onClick={() => setEditingId(null)} style={{ ...btnDanger, color: 'var(--tm)' }}>
              Cancel
            </button>
          </>
        ) : (
          <>
            <span
              style={{
                width: 14,
                height: 14,
                borderRadius: '50%',
                background: scope.color,
                flexShrink: 0,
              }}
            />
            <span style={{ flex: 1, fontSize: 13, fontWeight: indent ? 400 : 600 }}>
              {scope.name}
            </span>
            {!indent && (
              <button
                onClick={() => {
                  setAddingChildOf(addingChildOf === scope.id ? null : scope.id);
                  setNewName('');
                  setNewColor(
                    DEFAULT_SCOPE_COLORS[scopes.length % DEFAULT_SCOPE_COLORS.length] || '#059669'
                  );
                }}
                style={{
                  padding: '3px 8px',
                  borderRadius: 4,
                  border: '1px solid var(--bd)',
                  background: 'transparent',
                  color: 'var(--ac)',
                  fontSize: 10,
                  fontWeight: 600,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                }}
              >
                + Sub-Scope
              </button>
            )}
            <button onClick={() => startEdit(scope)} style={{ ...btnDanger, color: 'var(--tm)' }}>
              Edit
            </button>
            <button
              onClick={() => removeScope(scope.id)}
              style={btnDanger}
              title="Delete scope"
              aria-label={`Delete scope ${scope.name}`}
            >
              <Trash2 size={14} />
            </button>
          </>
        )}
      </div>
    );
  }

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 16,
        }}
      >
        <div style={{ fontSize: 13, color: 'var(--tm)' }}>
          Define project scopes (disciplines and sub-disciplines). Scopes can be assigned to
          projects.
        </div>
        <button
          onClick={() => {
            setAddingParent(true);
            setAddingChildOf(null);
            setNewName('');
            setNewColor(
              DEFAULT_SCOPE_COLORS[parentScopes.length % DEFAULT_SCOPE_COLORS.length] || '#2563eb'
            );
          }}
          style={btnPrimary}
        >
          + Add Scope
        </button>
      </div>

      {addingParent && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'center' }}>
          <input
            type="color"
            value={newColor}
            onChange={(e) => setNewColor(e.target.value)}
            style={{
              width: 32,
              height: 32,
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              padding: 0,
            }}
          />
          <input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && addScope()}
            placeholder="New scope name (e.g., Mechanical, Electrical)..."
            style={{ ...inputStyle, flex: 1 }}
            autoFocus
          />
          <button
            onClick={() => addScope()}
            disabled={!newName.trim()}
            style={{ ...btnPrimary, opacity: newName.trim() ? 1 : 0.5 }}
          >
            Add
          </button>
          <button
            onClick={() => setAddingParent(false)}
            style={{ ...btnDanger, color: 'var(--tm)', border: '1px solid var(--bd)' }}
          >
            Cancel
          </button>
        </div>
      )}

      <div
        style={{
          background: 'var(--sf)',
          borderRadius: 8,
          border: '1px solid var(--bd)',
          overflow: 'hidden',
        }}
      >
        {loading ? (
          <RowSkeleton label="Loading scopes" />
        ) : parentScopes.length === 0 && !addingParent ? (
          <EmptyState
            title="No scopes defined yet"
            subtitle='Add scopes like "Mechanical", "Electrical", "Plumbing", "Technology" to categorize project disciplines.'
          />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {parentScopes.map((parent) => (
              <React.Fragment key={parent.id}>
                {renderScopeRow(parent, false)}
                {childScopes(parent.id).map((child) => renderScopeRow(child, true))}
                {addingChildOf === parent.id && (
                  <div
                    style={{
                      display: 'flex',
                      gap: 8,
                      padding: '8px 12px',
                      paddingLeft: 40,
                      borderBottom: '1px solid var(--bd)',
                      background: 'rgba(var(--acr),0.03)',
                    }}
                  >
                    <input
                      type="color"
                      value={newColor}
                      onChange={(e) => setNewColor(e.target.value)}
                      style={{
                        width: 28,
                        height: 28,
                        border: 'none',
                        background: 'none',
                        cursor: 'pointer',
                        padding: 0,
                      }}
                    />
                    <input
                      value={newName}
                      onChange={(e) => setNewName(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && addScope(parent.id)}
                      placeholder={`Sub-scope under ${parent.name}...`}
                      style={{ ...inputStyle, flex: 1 }}
                      autoFocus
                    />
                    <button
                      onClick={() => addScope(parent.id)}
                      disabled={!newName.trim()}
                      style={{
                        ...btnPrimary,
                        padding: '5px 10px',
                        opacity: newName.trim() ? 1 : 0.5,
                      }}
                    >
                      Add
                    </button>
                    <button
                      onClick={() => setAddingChildOf(null)}
                      style={{ ...btnDanger, color: 'var(--tm)' }}
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── Firm Holidays Tab ─── */
function FirmHolidaysTab({ toast }: { toast: AdminPageProps['toast'] }) {
  const [items, setItems] = useState<FirmHoliday[]>([]);
  const [newName, setNewName] = useState('');
  const [newDate, setNewDate] = useState('');
  const [newHours, setNewHours] = useState('8');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [editingDate, setEditingDate] = useState('');
  const [editingHours, setEditingHours] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    load();
  }, []);
  useTableSync(['firm_holidays'], () => load());
  async function load() {
    try {
      const list = await VacationService.holidays.list();
      setItems(list.sort((a, b) => a.date.localeCompare(b.date)));
    } finally {
      setLoading(false);
    }
  }

  async function add() {
    if (!newName.trim() || !newDate) return;
    try {
      await VacationService.holidays.save({
        id: '',
        name: newName.trim(),
        date: newDate,
        hours: parseFloat(newHours) || 8,
        createdAt: '',
        updatedAt: '',
      });
      setNewName('');
      setNewDate('');
      setNewHours('8');
      await load();
      toast('Holiday added');
    } catch (err: any) {
      toast('Save failed: ' + err.message, true);
    }
  }

  async function update(id: string) {
    if (!editingName.trim() || !editingDate) return;
    const item = items.find((i) => i.id === id);
    if (item) {
      try {
        await VacationService.holidays.save({
          ...item,
          name: editingName.trim(),
          date: editingDate,
          hours: parseFloat(editingHours) || 8,
        });
        setEditingId(null);
        await load();
        toast('Holiday updated');
      } catch (err: any) {
        toast('Update failed: ' + err.message, true);
      }
    }
  }

  async function remove(id: string) {
    if (!confirm('Delete this holiday?')) return;
    try {
      await VacationService.holidays.delete(id);
      await load();
      toast('Holiday deleted');
    } catch (err: any) {
      toast('Delete failed: ' + err.message, true);
    }
  }

  return (
    <div>
      <div style={{ fontSize: 13, color: 'var(--tm)', marginBottom: 16 }}>
        Firm-wide holidays that apply to all team members automatically. These will appear on
        timesheets and reduce forecasting capacity.
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'flex-end' }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--tm)', marginBottom: 3 }}>
            Holiday Name
          </div>
          <input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && add()}
            placeholder="e.g. Christmas Day..."
            style={inputStyle}
          />
        </div>
        <div style={{ width: 160 }}>
          <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--tm)', marginBottom: 3 }}>
            Date
          </div>
          <input
            value={newDate}
            onChange={(e) => setNewDate(e.target.value)}
            type="date"
            style={inputStyle}
          />
        </div>
        <div style={{ width: 100 }}>
          <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--tm)', marginBottom: 3 }}>
            Hours
          </div>
          <input
            value={newHours}
            onChange={(e) => setNewHours(e.target.value)}
            type="number"
            step="0.5"
            min="0"
            max="24"
            style={{ ...inputStyle, fontFamily: "'JetBrains Mono', monospace" }}
          />
        </div>
        <button onClick={add} style={{ ...btnPrimary, marginBottom: 0 }}>
          + Add
        </button>
      </div>

      <div
        style={{
          background: 'var(--sf)',
          borderRadius: 8,
          border: '1px solid var(--bd)',
          overflow: 'hidden',
        }}
      >
        {loading ? (
          <RowSkeleton label="Loading firm holidays" />
        ) : items.length === 0 ? (
          <EmptyState title="No firm holidays defined yet" />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <div
              style={{
                display: 'flex',
                gap: 8,
                padding: '6px 12px',
                borderBottom: '1px solid var(--bd)',
                background: 'var(--sa)',
              }}
            >
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: 'var(--tm)',
                  textTransform: 'uppercase',
                  flex: 1,
                }}
              >
                Holiday
              </span>
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: 'var(--tm)',
                  textTransform: 'uppercase',
                  width: 120,
                  textAlign: 'center',
                }}
              >
                Date
              </span>
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: 'var(--tm)',
                  textTransform: 'uppercase',
                  width: 70,
                  textAlign: 'right',
                }}
              >
                Hours
              </span>
              <span style={{ width: 100 }}></span>
            </div>
            {items.map((item, i) => (
              <div
                key={item.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '8px 12px',
                  borderBottom: i < items.length - 1 ? '1px solid var(--bd)' : 'none',
                }}
              >
                {editingId === item.id ? (
                  <>
                    <input
                      value={editingName}
                      onChange={(e) => setEditingName(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && update(item.id)}
                      style={{ ...inputStyle, flex: 1 }}
                      autoFocus
                    />
                    <input
                      value={editingDate}
                      onChange={(e) => setEditingDate(e.target.value)}
                      type="date"
                      style={{ ...inputStyle, width: 120 }}
                    />
                    <input
                      value={editingHours}
                      onChange={(e) => setEditingHours(e.target.value)}
                      type="number"
                      step="0.5"
                      min="0"
                      max="24"
                      style={{
                        ...inputStyle,
                        width: 70,
                        fontFamily: "'JetBrains Mono', monospace",
                      }}
                    />
                    <button
                      onClick={() => update(item.id)}
                      style={{ ...btnPrimary, padding: '5px 10px' }}
                    >
                      Save
                    </button>
                    <button
                      onClick={() => setEditingId(null)}
                      style={{ ...btnDanger, color: 'var(--tm)' }}
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <>
                    <span style={{ flex: 1, fontSize: 13 }}>{item.name}</span>
                    <span style={{ width: 120, textAlign: 'center', fontSize: 12 }}>
                      {item.date}
                    </span>
                    <span
                      style={{
                        width: 70,
                        textAlign: 'right',
                        fontSize: 12,
                        fontFamily: "'JetBrains Mono', monospace",
                      }}
                    >
                      {item.hours}h
                    </span>
                    <button
                      onClick={() => {
                        setEditingId(item.id);
                        setEditingName(item.name);
                        setEditingDate(item.date);
                        setEditingHours(String(item.hours));
                      }}
                      style={{ ...btnDanger, color: 'var(--tm)' }}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => remove(item.id)}
                      style={btnDanger}
                      title="Delete holiday"
                      aria-label={`Delete holiday ${item.name}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── Expense Types Tab (with Rate column) ─── */
function ExpenseTypesTab({ toast }: { toast: AdminPageProps['toast'] }) {
  const [items, setItems] = useState<any[]>([]);
  const [newName, setNewName] = useState('');
  const [newRate, setNewRate] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [editingRate, setEditingRate] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    load();
  }, []);
  useTableSync(['expense_types'], () => load());
  async function load() {
    try {
      const rows = await AdminService.expenseTypes.list();
      rows.sort((a: any, b: any) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
      setItems(rows);
    } finally {
      setLoading(false);
    }
  }

  const drag = useDragReorder(items, async (reordered) => {
    setItems(reordered);
    try {
      await Promise.all(
        reordered.map((it: any, i) => {
          if ((items.find((x) => x.id === it.id)?.sortOrder ?? -1) !== i) {
            return AdminService.expenseTypes.save({ ...it, sortOrder: i });
          }
          return Promise.resolve();
        })
      );
    } catch (err: any) {
      toast('Reorder failed: ' + err.message, true);
      await load();
    }
  });

  async function add() {
    if (!newName.trim()) return;
    try {
      await AdminService.expenseTypes.save({
        id: '',
        name: newName.trim(),
        rate: newRate ? parseFloat(newRate) : undefined,
        sortOrder: items.length,
        createdAt: '',
        updatedAt: '',
      } as any);
      setNewName('');
      setNewRate('');
      await load();
      toast('Expense type added');
    } catch (err: any) {
      toast('Save failed: ' + err.message, true);
    }
  }

  async function update(id: string) {
    if (!editingName.trim()) return;
    const item = items.find((i) => i.id === id);
    if (item) {
      try {
        await AdminService.expenseTypes.save({
          ...item,
          name: editingName.trim(),
          rate: editingRate ? parseFloat(editingRate) : undefined,
        });
        setEditingId(null);
        await load();
        toast('Expense type updated');
      } catch (err: any) {
        toast('Update failed: ' + err.message, true);
      }
    }
  }

  async function remove(id: string) {
    if (!confirm('Delete this expense type?')) return;
    try {
      await AdminService.expenseTypes.delete(id);
      await load();
      toast('Expense type deleted');
    } catch (err: any) {
      toast('Delete failed: ' + err.message, true);
    }
  }

  return (
    <div>
      <div style={{ fontSize: 13, color: 'var(--tm)', marginBottom: 16 }}>
        Define expense types for expense reports. Set a rate for per-unit expenses (e.g., mileage
        $/mile). Leave rate blank for flat-amount expenses.
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'flex-end' }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--tm)', marginBottom: 3 }}>
            Type Name
          </div>
          <input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && add()}
            placeholder="e.g. Mileage..."
            style={inputStyle}
          />
        </div>
        <div style={{ width: 120 }}>
          <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--tm)', marginBottom: 3 }}>
            Rate ($/unit)
          </div>
          <input
            value={newRate}
            onChange={(e) => setNewRate(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && add()}
            placeholder="optional"
            type="number"
            step="0.01"
            min="0"
            style={{ ...inputStyle, fontFamily: "'JetBrains Mono', monospace" }}
          />
        </div>
        <button onClick={add} style={{ ...btnPrimary, marginBottom: 0 }}>
          + Add
        </button>
      </div>

      <div
        style={{
          background: 'var(--sf)',
          borderRadius: 8,
          border: '1px solid var(--bd)',
          overflow: 'hidden',
        }}
      >
        {loading ? (
          <RowSkeleton label="Loading expense types" />
        ) : items.length === 0 ? (
          <EmptyState
            title="No expense types defined"
            subtitle="Defaults will be seeded automatically."
          />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <div
              style={{
                display: 'flex',
                gap: 8,
                padding: '6px 12px',
                borderBottom: '1px solid var(--bd)',
                background: 'var(--sa)',
              }}
            >
              <span style={{ width: 24 }}></span>
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: 'var(--tm)',
                  textTransform: 'uppercase',
                  flex: 1,
                }}
              >
                Type
              </span>
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: 'var(--tm)',
                  textTransform: 'uppercase',
                  width: 100,
                  textAlign: 'right',
                }}
              >
                Rate
              </span>
              <span style={{ width: 100 }}></span>
            </div>
            {items.map((item, i) => {
              const isDragging = drag.dragIdx === i;
              const isDropTarget =
                drag.overIdx === i && drag.dragIdx !== null && drag.dragIdx !== i;
              return (
                <div
                  key={item.id}
                  {...drag.rowProps(i)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '8px 12px',
                    borderBottom: i < items.length - 1 ? '1px solid var(--bd)' : 'none',
                    background: isDragging
                      ? 'rgba(var(--acr),0.08)'
                      : isDropTarget
                        ? 'rgba(var(--acr),0.04)'
                        : undefined,
                    opacity: isDragging ? 0.5 : 1,
                    transition: 'background .1s',
                  }}
                >
                  <span
                    {...drag.handleProps()}
                    style={{ ...DRAG_HANDLE_STYLE, ...drag.handleProps().style }}
                    title="Drag to reorder"
                    aria-label="Drag to reorder"
                  >
                    <GripVertical size={14} />
                  </span>
                  {editingId === item.id ? (
                    <>
                      <input
                        value={editingName}
                        onChange={(e) => setEditingName(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && update(item.id)}
                        style={{ ...inputStyle, flex: 1 }}
                        autoFocus
                      />
                      <input
                        value={editingRate}
                        onChange={(e) => setEditingRate(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && update(item.id)}
                        placeholder="N/A"
                        type="number"
                        step="0.01"
                        min="0"
                        style={{
                          ...inputStyle,
                          width: 100,
                          fontFamily: "'JetBrains Mono', monospace",
                        }}
                      />
                      <button
                        onClick={() => update(item.id)}
                        style={{ ...btnPrimary, padding: '5px 10px' }}
                      >
                        Save
                      </button>
                      <button
                        onClick={() => setEditingId(null)}
                        style={{ ...btnDanger, color: 'var(--tm)' }}
                      >
                        Cancel
                      </button>
                    </>
                  ) : (
                    <>
                      <span style={{ flex: 1, fontSize: 13 }}>{item.name}</span>
                      <span
                        style={{
                          width: 100,
                          textAlign: 'right',
                          fontSize: 12,
                          fontFamily: "'JetBrains Mono', monospace",
                          color: item.rate != null ? 'var(--tx)' : 'var(--tm)',
                        }}
                      >
                        {item.rate != null ? `$${item.rate.toFixed(2)}/unit` : 'N/A'}
                      </span>
                      <button
                        onClick={() => {
                          setEditingId(item.id);
                          setEditingName(item.name);
                          setEditingRate(item.rate != null ? String(item.rate) : '');
                        }}
                        style={{ ...btnDanger, color: 'var(--tm)' }}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => remove(item.id)}
                        style={btnDanger}
                        title="Delete expense type"
                        aria-label={`Delete expense type ${item.name}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

(window as any).AdminPage = AdminPage;
export { AdminPage };
